import type { ChangeEvent, JSX, KeyboardEvent, MouseEvent } from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  AlertCircle,
  Check,
  CheckCircle2,
  ChevronRight,
  Circle,
  Edit2,
  Plus,
  Trash2,
  X,
} from 'lucide-react';

import { logger } from '~/utils/logger';

import { ConfirmationDialog } from '~/components/ConfirmationDialog';

import type { Subtask } from '~/types';

interface SubtaskListProps {
  taskId: string;
  subtasks: Subtask[]; // Use base Subtask type
  onSubtaskAdd: (taskId: string) => void;
  // Update prop type to only accept real Subtask properties
  onSubtaskUpdate: (
    taskId: string,
    subtaskId: string,
    updates: Partial<Omit<Subtask, 'id' | 'taskId' | 'createdAt' | 'updatedAt'>>,
  ) => void;
  onSubtaskDelete: (taskId: string, subtaskId: string) => void;
  isLoading?: boolean;
  error?: Error | null;
}

export function SubtaskList(props: SubtaskListProps): JSX.Element {
  const {
    error = null,
    isLoading = false,
    onSubtaskAdd,
    onSubtaskDelete,
    onSubtaskUpdate,
    subtasks,
    taskId,
  } = props;
  const [isExpanded, setIsExpanded] = useState(false);
  const [editingSubtaskId, setEditingSubtaskId] = useState<string | null>(null);
  const [localTitles, setLocalTitles] = useState<Record<string, string>>({});
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null); // Ref for autofocus

  // Ensure subtasksToShow is memoized to avoid recreation on each render
  const subtasksToShow = useMemo(() => subtasks || [], [subtasks]);

  // --- Simplified Effect for Temp Subtasks ---
  // Automatically enter edit mode for a newly added temporary subtask
  useEffect(() => {
    const tempSubtask = subtasks.find(s => s.id.startsWith('temp-'));
    if (tempSubtask && editingSubtaskId !== tempSubtask.id) {
      logger.debug({ component: 'SubtaskList' }, 'Auto-editing new temp subtask', {
        subtaskId: tempSubtask.id,
      });
      setEditingSubtaskId(tempSubtask.id);
      setLocalTitles(prev => ({
        ...prev,
        [tempSubtask.id]: tempSubtask.title ?? '',
      }));
      setIsExpanded(true); // Ensure list is expanded for temp subtasks
    }
  }, [subtasks, editingSubtaskId]);

  // Focus input when editing starts
  useEffect(() => {
    if (editingSubtaskId && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select(); // Select text for easy replacement
    }
  }, [editingSubtaskId]);
  // --- End Effects ---

  // --- Event Handlers ---
  const handleEditClick = useCallback((subtask: Subtask) => {
    logger.debug({ component: 'SubtaskList' }, 'Edit button clicked', {
      subtaskId: subtask.id,
    });
    setEditingSubtaskId(subtask.id);
    setLocalTitles(prev => ({ ...prev, [subtask.id]: subtask.title }));
    setIsExpanded(true); // Ensure list is expanded when editing starts
  }, []);

  const handleSave = useCallback(
    (subtaskId: string) => {
      const originalSubtask = subtasks.find(s => s.id === subtaskId);
      if (!originalSubtask) {
        return;
      }

      const newTitle = localTitles[subtaskId]?.trim();
      logger.debug({ component: 'SubtaskList' }, 'Saving subtask', {
        subtaskId,
        newTitle,
      });

      if (!newTitle) {
        // If title is empty, delete the subtask (especially important for temp ones)
        logger.debug({ component: 'SubtaskList' }, 'Deleting subtask due to empty title', {
          subtaskId,
        });
        onSubtaskDelete(taskId, subtaskId);
      } else if (newTitle !== originalSubtask.title) {
        // Only call update if the title has actually changed
        logger.debug({ component: 'SubtaskList' }, 'Calling onSubtaskUpdate', {
          subtaskId,
        });
        onSubtaskUpdate(taskId, subtaskId, { title: newTitle });
      } else {
        logger.debug({ component: 'SubtaskList' }, 'No changes detected, exiting edit mode', {
          subtaskId,
        });
      }

      setEditingSubtaskId(null); // Exit edit mode regardless of change
      setLocalTitles(prev => {
        // Clean up local title state
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { [subtaskId]: _, ...rest } = prev;
        return rest;
      });
    },
    [localTitles, onSubtaskUpdate, onSubtaskDelete, taskId, subtasks],
  );

  const handleCancel = useCallback(
    (subtaskId: string) => {
      logger.debug({ component: 'SubtaskList' }, 'Cancelling edit for subtask', { subtaskId });
      const originalSubtask = subtasks.find(s => s.id === subtaskId);

      // If it was a temporary subtask and had no valid title, delete it on cancel
      if (
        originalSubtask?.id.startsWith('temp-') &&
        !originalSubtask?.title?.trim() &&
        !localTitles[subtaskId]?.trim()
      ) {
        logger.debug({ component: 'SubtaskList' }, 'Deleting empty temp subtask on cancel', {
          subtaskId,
        });
        onSubtaskDelete(taskId, subtaskId);
      }

      setEditingSubtaskId(null);
      setLocalTitles(prev => {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { [subtaskId]: _, ...rest } = prev;
        return rest;
      });
    },
    [onSubtaskDelete, taskId, subtasks, localTitles],
  );

  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLInputElement>, subtaskId: string) => {
      if (event.key === 'Enter') {
        handleSave(subtaskId);
      } else if (event.key === 'Escape') {
        handleCancel(subtaskId);
      }
    },
    [handleSave, handleCancel],
  );

  const handleToggleComplete = useCallback(
    (subtask: Subtask) => {
      logger.debug({ component: 'SubtaskList' }, 'Toggling complete', {
        subtaskId: subtask.id,
      });
      onSubtaskUpdate(taskId, subtask.id, { completed: !subtask.completed });
    },
    [onSubtaskUpdate, taskId],
  );

  const handleLocalTitleChange = useCallback((subtaskId: string, value: string) => {
    setLocalTitles(prev => ({ ...prev, [subtaskId]: value }));
  }, []);

  const handleAddClick = useCallback(
    (e: MouseEvent) => {
      logger.debug({ component: 'SubtaskList' }, 'Add button clicked');
      e.stopPropagation();
      onSubtaskAdd(taskId); // This should trigger the useEffect for temp subtasks
    },
    [onSubtaskAdd, taskId],
  );

  const handleToggleExpand = useCallback((e: MouseEvent) => {
    e.stopPropagation();
    setIsExpanded(prev => !prev);
  }, []);

  const handleDeleteClick = useCallback(
    (subtaskId: string) => {
      // For temporary subtasks, delete immediately without confirmation
      if (subtaskId.startsWith('temp-')) {
        onSubtaskDelete(taskId, subtaskId);
      } else {
        setShowDeleteConfirm(subtaskId);
      }
    },
    [taskId, onSubtaskDelete],
  );

  const handleConfirmDelete = useCallback(() => {
    if (showDeleteConfirm) {
      onSubtaskDelete(taskId, showDeleteConfirm);
      setShowDeleteConfirm(null);
    }
  }, [onSubtaskDelete, taskId, showDeleteConfirm]);

  const handleCancelDelete = useCallback(() => {
    setShowDeleteConfirm(null);
  }, []);

  // Create event handlers for the subtasks

  const createToggleHandler = useCallback(
    (subtask: Subtask) =>
      function handleSubtaskToggle(): void {
        handleToggleComplete(subtask);
      },
    [handleToggleComplete],
  );

  const createEditClickHandler = useCallback(
    (subtask: Subtask) =>
      function handleSubtaskEdit(): void {
        handleEditClick(subtask);
      },
    [handleEditClick],
  );

  const createDeleteClickHandler = useCallback(
    (subtaskId: string) =>
      function handleSubtaskDelete(): void {
        handleDeleteClick(subtaskId);
      },
    [handleDeleteClick],
  );

  const createSaveHandler = useCallback(
    (subtaskId: string) =>
      function handleSubtaskSave(): void {
        handleSave(subtaskId);
      },
    [handleSave],
  );

  const createCancelHandler = useCallback(
    (subtaskId: string) =>
      function handleSubtaskCancel(): void {
        handleCancel(subtaskId);
      },
    [handleCancel],
  );

  const createKeyDownHandler = useCallback(
    (subtaskId: string) =>
      function handleSubtaskKeyDown(e: KeyboardEvent<HTMLInputElement>): void {
        handleKeyDown(e, subtaskId);
      },
    [handleKeyDown],
  );

  const createChangeHandler = useCallback(
    (subtaskId: string) =>
      function handleSubtaskChange(e: ChangeEvent<HTMLInputElement>): void {
        handleLocalTitleChange(subtaskId, e.target.value);
      },
    [handleLocalTitleChange],
  );

  // Pre-create handlers for each subtask to avoid inline function creation
  const subtaskHandlers = useMemo(() => {
    return subtasksToShow.reduce(
      (acc, subtask) => {
        acc[subtask.id] = {
          toggle: createToggleHandler(subtask),
          edit: createEditClickHandler(subtask),
          delete: createDeleteClickHandler(subtask.id),
          save: createSaveHandler(subtask.id),
          cancel: createCancelHandler(subtask.id),
          keyDown: createKeyDownHandler(subtask.id),
          change: createChangeHandler(subtask.id),
        };
        return acc;
      },
      {} as Record<
        string,
        {
          cancel: () => void;
          change: (e: ChangeEvent<HTMLInputElement>) => void;
          delete: () => void;
          edit: () => void;
          keyDown: (e: KeyboardEvent<HTMLInputElement>) => void;
          save: () => void;
          toggle: () => void;
        }
      >,
    );
  }, [
    subtasksToShow,
    createToggleHandler,
    createEditClickHandler,
    createDeleteClickHandler,
    createSaveHandler,
    createCancelHandler,
    createKeyDownHandler,
    createChangeHandler,
  ]);

  // Early return for error state
  if (error) {
    return (
      <div className="p-2 text-sm text-error dark:text-error bg-error/10 dark:bg-error/10 rounded flex items-center gap-2">
        <AlertCircle className="w-4 h-4" />
        <span>Error loading subtasks: {error.message}</span>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {/* Header */}
      <div className="flex items-center justify-between">
        <button
          type="button"
          onClick={handleToggleExpand}
          className="flex items-center gap-1 text-sm text-foreground-600 dark:text-muted hover:text-foreground transition-colors"
          disabled={isLoading}
        >
          <ChevronRight
            className={`w-4 h-4 transition-transform duration-150 ${isExpanded ? 'rotate-90' : ''}`}
          />
          Subtasks ({subtasksToShow.filter(s => s.completed).length}/{subtasksToShow.length})
          {isLoading && <span className="ml-2 text-xs">(Loading...)</span>}
        </button>
        <button
          type="button"
          onClick={handleAddClick}
          aria-label="Add subtask"
          className="p-1 text-foreground-600 dark:text-muted hover:text-foreground rounded hover:bg-primary/10 dark:hover:bg-primary/20 disabled:opacity-50 disabled:cursor-not-allowed"
          disabled={isLoading}
        >
          <Plus className="w-4 h-4" />
        </button>
      </div>

      {/* Progress bar */}
      {subtasksToShow.length > 0 && (
        <div className="h-2 bg-surface-elevated dark:bg-surface-elevated rounded-full overflow-hidden">
          <div
            className="h-full bg-primary dark:bg-primary rounded-full"
            style={{
              width: `${Math.round(
                (subtasksToShow.filter(s => s.completed).length / subtasksToShow.length) * 100,
              )}%`,
            }}
          />
        </div>
      )}

      {/* Subtask List */}
      {isExpanded && (
        <div className="space-y-2">
          {subtasksToShow.map(subtask => (
            <div key={subtask.id} className="flex items-center gap-2 group">
              {/* Completion Toggle */}
              <button
                type="button"
                onClick={subtaskHandlers[subtask.id].toggle}
                className="shrink-0"
                disabled={isLoading || editingSubtaskId === subtask.id}
              >
                {subtask.completed ? (
                  <CheckCircle2 className="w-4 h-4 text-success dark:text-success" />
                ) : (
                  <Circle className="w-4 h-4 text-muted" />
                )}
              </button>

              {/* Content Area: Either Editing Input or Display Span */}
              {editingSubtaskId === subtask.id ? (
                // --- Edit Mode ---
                <div className="flex-1 flex items-center gap-2">
                  <input
                    ref={inputRef}
                    type="text"
                    value={localTitles[subtask.id] ?? ''}
                    onChange={subtaskHandlers[subtask.id].change}
                    onKeyDown={subtaskHandlers[subtask.id].keyDown}
                    onBlur={subtaskHandlers[subtask.id].save}
                    className="flex-1 px-2 py-1 text-sm border rounded bg-surface text-foreground focus:outline-none focus:ring-2 focus:ring-primary dark:focus:ring-primary/50 border-default"
                    placeholder="Subtask title"
                    disabled={isLoading}
                  />
                  {/* Edit Actions (Save/Cancel) */}
                  <div className="flex items-center gap-1">
                    <button
                      type="button"
                      onClick={subtaskHandlers[subtask.id].save}
                      className="p-1 hover:bg-success/10 dark:hover:bg-success/10 rounded"
                      disabled={isLoading}
                      title="Save Subtask"
                    >
                      <Check className="w-4 h-4 text-success dark:text-success" />
                    </button>
                    <button
                      type="button"
                      onClick={subtaskHandlers[subtask.id].cancel}
                      className="p-1 hover:bg-error/10 dark:hover:bg-error/10 rounded"
                      disabled={isLoading}
                      title="Cancel Edit"
                    >
                      <X className="w-4 h-4 text-error dark:text-error" />
                    </button>
                  </div>
                </div>
              ) : (
                // --- Display Mode ---
                <div className="flex-1 flex items-center justify-between">
                  <span
                    className={`text-sm cursor-pointer ${
                      subtask.completed
                        ? 'text-muted line-through'
                        : 'text-foreground-600 dark:text-muted'
                    }`}
                  >
                    {subtask.title || <span className="text-muted italic">Untitled</span>}
                  </span>
                  {/* Display Actions (Edit/Delete) - Show on hover */}
                  <div className="hidden group-hover:flex items-center gap-1">
                    <button
                      type="button"
                      onClick={subtaskHandlers[subtask.id].edit}
                      className="p-1 hover:bg-surface-elevated dark:hover:bg-surface-elevated rounded"
                      disabled={isLoading}
                      title="Edit Subtask"
                    >
                      <Edit2 className="w-4 h-4 text-foreground-600 dark:text-muted" />
                    </button>
                    <button
                      type="button"
                      onClick={subtaskHandlers[subtask.id].delete}
                      className="p-1 hover:bg-error/10 dark:hover:bg-error/10 rounded"
                      disabled={isLoading}
                      title="Delete Subtask"
                    >
                      <Trash2 className="w-4 h-4 text-error dark:text-error" />
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {showDeleteConfirm && (
        <ConfirmationDialog
          content="Are you sure you want to delete this subtask? This action cannot be undone."
          isOpen={!!showDeleteConfirm}
          onConfirm={handleConfirmDelete}
          onClose={handleCancelDelete}
          title="Delete Subtask"
        />
      )}
    </div>
  );
}
